import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as d3 from 'd3';

const RANK_COLORS = {
  mercure: '#9CA3AF', venus: '#FBBF24', terre: '#60A5FA',
  mars: '#F87171', jupiter: '#FB923C', saturne: '#A78BFA',
};
const POSITION_COLORS_4x7 = { N: '#4ADE80', E: '#60A5FA', S: '#FB923C', W: '#C084FC' };
const POSITION_ANGLES_4x7 = { N: -Math.PI / 2, E: 0, S: Math.PI / 2, W: Math.PI };
const POSITION_SECTORS_4x7 = {
  N: [-3 * Math.PI / 4, -Math.PI / 4],
  E: [-Math.PI / 4, Math.PI / 4],
  S: [Math.PI / 4, 3 * Math.PI / 4],
  W: [3 * Math.PI / 4, 5 * Math.PI / 4],
};

function processTree(tree, matrixWidth) {
  const nodes = [];
  const links = [];

  if (!tree) return { nodes, links };

  // Root node
  nodes.push({
    id: tree.user_id, name: tree.name, rank: tree.rank, position: null,
    level: 0, angle: 0, isActive: tree.is_active !== false,
    childrenCount: tree.children_count || 0, totalDownline: tree.total_downline || 0,
    isRoot: true, isEmpty: false, orbit: 0,
  });

  function addChildren(parentNode, parentIdx, children, level, sectorStart, sectorEnd) {
    if (!children || children.length === 0) return;

    // For 4x7 at level 1, position determines angle
    if (matrixWidth === 4 && level === 1) {
      const allPositions = ['N', 'E', 'S', 'W'];
      const filledPositions = new Set(children.map(c => c.position));

      allPositions.forEach(pos => {
        const child = children.find(c => c.position === pos);
        const angle = POSITION_ANGLES_4x7[pos];
        const [secStart, secEnd] = POSITION_SECTORS_4x7[pos];
        const idx = nodes.length;

        if (child) {
          nodes.push({
            id: child.user_id, name: child.name, rank: child.rank,
            position: pos, level, angle, isActive: child.is_active !== false,
            childrenCount: child.children_count || 0, totalDownline: child.total_downline || 0,
            isRoot: false, isEmpty: false, orbit: child.orbit || level,
          });
          links.push({ source: parentIdx, target: idx });
          if (child.children?.length > 0) {
            addChildren(nodes[idx], idx, child.children, level + 1, secStart, secEnd);
          }
        } else {
          nodes.push({
            id: `empty-${pos}-${level}`, name: '', rank: null,
            position: pos, level, angle, isActive: false,
            childrenCount: 0, totalDownline: 0, isRoot: false, isEmpty: true, orbit: level,
          });
          links.push({ source: parentIdx, target: idx });
        }
      });
    } else {
      // General distribution: divide sector among children
      const expectedCount = matrixWidth;
      const sectorSize = (sectorEnd - sectorStart) / expectedCount;

      // Fill positions
      for (let i = 0; i < expectedCount; i++) {
        const child = children[i];
        const childSectorStart = sectorStart + i * sectorSize;
        const childSectorEnd = childSectorStart + sectorSize;
        const childAngle = (childSectorStart + childSectorEnd) / 2;
        const idx = nodes.length;

        if (child) {
          nodes.push({
            id: child.user_id, name: child.name, rank: child.rank,
            position: child.position || (matrixWidth === 2 ? (i === 0 ? 'L' : 'R') : `P${i + 1}`),
            level, angle: childAngle, isActive: child.is_active !== false,
            childrenCount: child.children_count || 0, totalDownline: child.total_downline || 0,
            isRoot: false, isEmpty: false, orbit: child.orbit || level,
          });
          links.push({ source: parentIdx, target: idx });
          if (child.children?.length > 0) {
            addChildren(nodes[idx], idx, child.children, level + 1, childSectorStart, childSectorEnd);
          }
        } else if (level <= 2) {
          // Show empty slots only for first 2 levels
          nodes.push({
            id: `empty-${i}-${level}-${parentIdx}`, name: '', rank: null,
            position: matrixWidth === 2 ? (i === 0 ? 'L' : 'R') : `P${i + 1}`,
            level, angle: childAngle, isActive: false,
            childrenCount: 0, totalDownline: 0, isRoot: false, isEmpty: true, orbit: level,
          });
          links.push({ source: parentIdx, target: idx });
        }
      }
    }
  }

  // Process root children
  const children = tree.children || [];
  if (matrixWidth === 4) {
    addChildren(nodes[0], 0, children, 1, -Math.PI, Math.PI);
  } else {
    addChildren(nodes[0], 0, children, 1, -Math.PI, Math.PI);
  }

  return { nodes, links };
}

const OrbitTreeD3 = ({
  tree, matrixWidth = 4, systemType = '4x7',
  onDrillDown, language = 'fr', maxDepth = 5
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [renderKey, setRenderKey] = useState(0);

  const t = useCallback((fr, en) => language === 'fr' ? fr : en, [language]);

  // Resize observer triggers re-render
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => {
      setRenderKey(k => k + 1);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Main D3 rendering
  useEffect(() => {
    const svgEl = svgRef.current;
    const containerEl = containerRef.current;
    if (!svgEl || !containerEl || !tree) return;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    const rect = containerEl.getBoundingClientRect();
    const width = Math.max(400, rect.width);
    const height = Math.max(400, rect.height);
    svgEl.setAttribute('width', width);
    svgEl.setAttribute('height', height);
    const cx = width / 2;
    const cy = height / 2;
    const { nodes, links } = processTree(tree, matrixWidth);
    const maxLevel = Math.max(...nodes.map(n => n.level), 1);
    const ringSpacing = Math.min(width, height) / (maxLevel * 2 + 3);

    // --- 4x7: elliptical perspective; 2x15: circular ---
    const is4x7 = matrixWidth === 4;
    const TILT = is4x7 ? 0.55 : 1;

    // Zoom behavior
    const zoomG = svg.append('g').attr('class', 'zoom-root');
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.2, 4])
      .on('zoom', (event) => {
        zoomG.attr('transform', event.transform);
      });
    svg.call(zoomBehavior);

    // Auto-fit: scale to show all orbits centered
    const totalRadius = (maxLevel + 1.5) * ringSpacing;
    const fitScale = Math.min(width, height) / (totalRadius * 2.2);
    const initialScale = Math.min(fitScale, 1);
    svg.call(zoomBehavior.transform, d3.zoomIdentity.translate(cx, cy).scale(initialScale));

    // === DEFS (gradients, filters) ===
    const defs = svg.append('defs');

    // Glow filter for center node
    const glow = defs.append('filter').attr('id', 'glow');
    glow.append('feGaussianBlur').attr('stdDeviation', '5').attr('result', 'blur');
    glow.append('feMerge').selectAll('feMergeNode')
      .data(['blur', 'SourceGraphic']).enter()
      .append('feMergeNode').attr('in', d => d);

    // Soft glow for rings
    const ringGlow = defs.append('filter').attr('id', 'ring-glow')
      .attr('x', '-50%').attr('y', '-50%').attr('width', '200%').attr('height', '200%');
    ringGlow.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'blur');
    ringGlow.append('feMerge').selectAll('feMergeNode')
      .data(['blur', 'SourceGraphic']).enter()
      .append('feMergeNode').attr('in', d => d);

    // Center gradient — brighter
    const centerGrad = defs.append('radialGradient').attr('id', 'center-grad');
    centerGrad.append('stop').attr('offset', '0%').attr('stop-color', '#A5B4FC');
    centerGrad.append('stop').attr('offset', '100%').attr('stop-color', '#6366F1');

    // Background: subtle radial glow from center
    const bgGrad = defs.append('radialGradient').attr('id', 'bg-glow');
    bgGrad.append('stop').attr('offset', '0%').attr('stop-color', '#1a1a3e').attr('stop-opacity', '1');
    bgGrad.append('stop').attr('offset', '100%').attr('stop-color', '#0B0E17').attr('stop-opacity', '1');
    zoomG.insert('circle', ':first-child')
      .attr('r', (maxLevel + 2) * ringSpacing)
      .attr('fill', 'url(#bg-glow)');

    // === ORBIT RINGS ===
    const ringsG = zoomG.append('g').attr('class', 'rings');

    const orbitColors = [
      '#818CF8', '#6366F1', '#60A5FA', '#38BDF8', '#2DD4BF',
      '#34D399', '#A78BFA', '#F472B6', '#FB923C', '#FBBF24',
      '#A3E635', '#818CF8', '#60A5FA', '#2DD4BF', '#A78BFA',
    ];

    for (let i = 1; i <= maxLevel; i++) {
      const rx = i * ringSpacing;
      const ry = rx * TILT;
      const color = orbitColors[(i - 1) % orbitColors.length];
      const baseOpacity = Math.max(0.18, 0.45 - i * 0.025);

      if (is4x7) {
        // Elliptical orbit ring
        ringsG.append('ellipse')
          .attr('rx', 0).attr('ry', 0)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-opacity', baseOpacity)
          .attr('stroke-width', i <= 3 ? 2.5 : 1.5)
          .attr('filter', i <= 3 ? 'url(#ring-glow)' : null)
          .transition().duration(600).delay(i * 100)
          .attr('rx', rx).attr('ry', ry);

        if (i <= 4) {
          ringsG.append('ellipse')
            .attr('rx', 0).attr('ry', 0)
            .attr('fill', color).attr('fill-opacity', 0.025)
            .attr('stroke', 'none')
            .transition().duration(600).delay(i * 100)
            .attr('rx', rx).attr('ry', ry);
        }

        // Orbit number at top-right
        const lAngle = -Math.PI / 4;
        ringsG.append('text')
          .attr('x', rx * Math.cos(lAngle) + 6)
          .attr('y', ry * Math.sin(lAngle) - 4)
          .attr('fill', color)
          .attr('fill-opacity', Math.max(0.35, 0.65 - i * 0.05))
          .attr('font-size', 9).attr('font-weight', '600')
          .attr('font-family', "'Satoshi', sans-serif")
          .text(`${i}`)
          .attr('opacity', 0)
          .transition().duration(400).delay(i * 100 + 300)
          .attr('opacity', 1);
      } else {
        // Circular orbit ring for 2x15
        ringsG.append('circle')
          .attr('r', 0)
          .attr('fill', 'none')
          .attr('stroke', color)
          .attr('stroke-opacity', baseOpacity)
          .attr('stroke-width', i <= 3 ? 2 : 1.5)
          .attr('filter', i <= 3 ? 'url(#ring-glow)' : null)
          .transition().duration(600).delay(i * 100)
          .attr('r', rx);

        if (i <= 4) {
          ringsG.append('circle')
            .attr('r', 0)
            .attr('fill', color).attr('fill-opacity', 0.02)
            .attr('stroke', 'none')
            .transition().duration(600).delay(i * 100)
            .attr('r', rx);
        }

        ringsG.append('text')
          .attr('x', rx + 8).attr('y', -8)
          .attr('fill', color)
          .attr('fill-opacity', Math.max(0.35, 0.6 - i * 0.04))
          .attr('font-size', 10).attr('font-weight', '600')
          .attr('font-family', "'Satoshi', sans-serif")
          .text(`${t('Orbite', 'Orbit')} ${i}`)
          .attr('opacity', 0)
          .transition().duration(400).delay(i * 100 + 300)
          .attr('opacity', 1);
      }
    }

    // === CARDINAL AXES ===
    if (is4x7) {
      const axisLen = (maxLevel + 0.8) * ringSpacing;
      const axesG = zoomG.append('g').attr('class', 'axes');
      const dirs = [
        { label: 'N', dx: 0, dy: -axisLen * TILT, color: POSITION_COLORS_4x7.N },
        { label: 'E', dx: axisLen, dy: 0, color: POSITION_COLORS_4x7.E },
        { label: 'S', dx: 0, dy: axisLen * TILT, color: POSITION_COLORS_4x7.S },
        { label: 'W', dx: -axisLen, dy: 0, color: POSITION_COLORS_4x7.W },
      ];

      dirs.forEach(d => {
        // Axis line
        axesG.append('line')
          .attr('x1', 0).attr('y1', 0)
          .attr('x2', d.dx).attr('y2', d.dy)
          .attr('stroke', d.color)
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '8,4')
          .attr('stroke-opacity', 0.35);

        // Arrow tip
        const tipLen = 10;
        const angle = Math.atan2(d.dy, d.dx);
        axesG.append('polygon')
          .attr('points', [
            [d.dx, d.dy],
            [d.dx - tipLen * Math.cos(angle - 0.3), d.dy - tipLen * Math.sin(angle - 0.3)],
            [d.dx - tipLen * Math.cos(angle + 0.3), d.dy - tipLen * Math.sin(angle + 0.3)],
          ].map(p => p.join(',')).join(' '))
          .attr('fill', d.color).attr('opacity', 0.6);

        // Direction label badge
        const lx = d.dx * 1.12;
        const ly = d.dy * 1.12;
        axesG.append('circle')
          .attr('cx', lx).attr('cy', ly).attr('r', 16)
          .attr('fill', d.color).attr('fill-opacity', 0.15)
          .attr('stroke', d.color).attr('stroke-opacity', 0.4).attr('stroke-width', 1.5);
        axesG.append('text')
          .attr('x', lx).attr('y', ly)
          .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
          .attr('fill', d.color).attr('font-size', 14).attr('font-weight', 'bold')
          .attr('font-family', "'Satoshi', sans-serif")
          .text(d.label)
          .attr('opacity', 0)
          .transition().duration(500).delay(800)
          .attr('opacity', 0.9);
      });
    } else {
      // For 2x15, show L/R labels
      const compassR = (maxLevel + 0.7) * ringSpacing;
      const compassG = zoomG.append('g').attr('class', 'compass');
      [{ label: 'L', angle: Math.PI }, { label: 'R', angle: 0 }].forEach(d => {
        const x = compassR * Math.cos(d.angle);
        const y = compassR * Math.sin(d.angle);
        compassG.append('text')
          .attr('x', x).attr('y', y)
          .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
          .attr('fill', 'rgba(99, 102, 241, 0.5)').attr('font-size', 14).attr('font-weight', 'bold')
          .text(d.label)
          .attr('opacity', 0).transition().duration(500).delay(800).attr('opacity', 0.5);
      });
    }

    // Compute x, y for each node (elliptical Y for 4x7)
    nodes.forEach(n => {
      if (n.level === 0) {
        n.x = 0; n.y = 0;
      } else {
        const r = n.level * ringSpacing;
        n.x = r * Math.cos(n.angle);
        n.y = r * Math.sin(n.angle) * TILT;
      }
    });

    // === LINKS ===
    const linksG = zoomG.append('g').attr('class', 'links');
    links.forEach((link, i) => {
      const src = nodes[link.source];
      const tgt = nodes[link.target];
      if (!src || !tgt) return;

      const tgtColor = matrixWidth === 4
        ? (POSITION_COLORS_4x7[tgt.position] || '#6366F1')
        : (RANK_COLORS[tgt.rank] || '#6366F1');

      linksG.append('line')
        .attr('x1', src.x).attr('y1', src.y)
        .attr('x2', src.x).attr('y2', src.y)
        .attr('stroke', tgt.isEmpty ? 'rgba(75, 85, 99, 0.15)' : tgtColor)
        .attr('stroke-opacity', tgt.isEmpty ? 0.2 : 0.25)
        .attr('stroke-width', tgt.isEmpty ? 0.5 : 1.5)
        .attr('stroke-dasharray', tgt.isEmpty ? '3,3' : 'none')
        .transition().duration(500).delay(tgt.level * 120 + i * 5)
        .attr('x2', tgt.x).attr('y2', tgt.y);
    });

    // === NODES ===
    const nodesG = zoomG.append('g').attr('class', 'nodes');

    // Render non-root nodes first
    const memberNodes = nodes.filter(n => !n.isRoot);
    memberNodes.forEach((n, i) => {
      const nodeG = nodesG.append('g')
        .attr('class', `node ${n.isEmpty ? 'empty' : 'filled'}`)
        .attr('transform', `translate(0, 0)`)
        .style('cursor', n.isEmpty ? 'default' : 'pointer');

      const nodeR = n.isEmpty ? 6 : Math.max(7, 16 - n.level * 1.5);

      if (n.isEmpty) {
        // Empty slot: dashed circle
        nodeG.append('circle')
          .attr('r', 0)
          .attr('fill', 'rgba(31, 41, 55, 0.5)')
          .attr('stroke', 'rgba(75, 85, 99, 0.3)')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,2');

        if (n.level <= 2) {
          nodeG.append('text')
            .attr('y', nodeR + 12)
            .attr('text-anchor', 'middle')
            .attr('fill', 'rgba(75, 85, 99, 0.4)')
            .attr('font-size', 8)
            .text(n.position || '');
        }
      } else {
        // Filled node
        const color = matrixWidth === 4
          ? (POSITION_COLORS_4x7[n.position] || RANK_COLORS[n.rank] || '#6B7280')
          : (RANK_COLORS[n.rank] || '#6B7280');

        // Outer glow ring for active nodes
        if (n.isActive && n.level <= 4) {
          nodeG.append('circle')
            .attr('r', 0).attr('fill', color).attr('opacity', 0.2);
        }

        // Main circle with brighter fill
        nodeG.append('circle')
          .attr('r', 0)
          .attr('fill', n.isActive ? color : '#374151')
          .attr('stroke', n.isActive ? 'rgba(255,255,255,0.35)' : 'rgba(75,85,99,0.3)')
          .attr('stroke-width', n.level <= 2 ? 2 : 1.5);

        // Name label
        if (n.level <= 4) {
          const lbl = n.name?.length > 7 ? n.name.substring(0, 6) + '..' : (n.name || '');
          nodeG.append('text')
            .attr('y', nodeR + 13)
            .attr('text-anchor', 'middle')
            .attr('fill', 'rgba(220, 220, 230, 0.85)')
            .attr('font-size', Math.max(8, 11 - n.level))
            .attr('font-weight', n.level <= 2 ? '600' : '400')
            .attr('font-family', "'Satoshi', sans-serif")
            .text(lbl)
            .attr('opacity', 0);
        }

        // Position badge (level 1-2)
        if (n.level <= 2 && n.position) {
          nodeG.append('text')
            .attr('y', -(nodeR + 6))
            .attr('text-anchor', 'middle')
            .attr('fill', color)
            .attr('font-size', 8).attr('font-weight', 'bold')
            .text(n.position)
            .attr('opacity', 0);
        }

        // Downline count badge (level 1)
        if (n.level === 1 && n.totalDownline > 0) {
          const badgeG = nodeG.append('g')
            .attr('transform', `translate(${nodeR + 2}, ${-nodeR + 2})`);
          badgeG.append('circle')
            .attr('r', 7).attr('fill', '#1E293B').attr('stroke', color).attr('stroke-width', 0.5);
          badgeG.append('text')
            .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
            .attr('fill', color).attr('font-size', 7).attr('font-weight', 'bold')
            .text(n.totalDownline > 999 ? `${Math.round(n.totalDownline / 1000)}k` : n.totalDownline);
          badgeG.attr('opacity', 0);
        }

        // Interaction
        nodeG
          .on('mouseenter', function (event) {
            d3.select(this).select('circle').transition().duration(150)
              .attr('r', nodeR * 1.3).attr('stroke-width', 2);

            const svgRect = svgRef.current.getBoundingClientRect();
            setTooltip({
              x: event.clientX - svgRect.left,
              y: event.clientY - svgRect.top - 10,
              data: n
            });
          })
          .on('mouseleave', function () {
            d3.select(this).select('circle').transition().duration(150)
              .attr('r', nodeR).attr('stroke-width', 1);
            setTooltip(null);
          })
          .on('click', () => {
            if (onDrillDown && !n.isEmpty) {
              onDrillDown(n.id);
            }
          });
      }

      // Animate entrance
      nodeG.transition().duration(500).delay(n.level * 120 + i * 8)
        .attr('transform', `translate(${n.x}, ${n.y})`);

      nodeG.selectAll('circle')
        .transition().duration(400).delay(n.level * 120 + i * 8 + 100)
        .attr('r', function () {
          const isGlow = d3.select(this).attr('opacity') === '0.15';
          return isGlow ? nodeR + 4 : nodeR;
        });

      nodeG.selectAll('text')
        .transition().duration(300).delay(n.level * 120 + i * 8 + 250)
        .attr('opacity', 1);

      // Downline badge
      nodeG.selectAll('g')
        .transition().duration(300).delay(n.level * 120 + i * 8 + 350)
        .attr('opacity', 1);
    });

    // === CENTER NODE (ROOT) ===
    const rootNode = nodes[0];
    if (rootNode) {
      const rootG = nodesG.append('g')
        .attr('class', 'root-node')
        .attr('transform', 'translate(0, 0)')
        .style('cursor', 'default');

      const rootR = 28;

      // Outer glow halo
      rootG.append('circle')
        .attr('r', 0)
        .attr('fill', 'none')
        .attr('stroke', '#818CF8')
        .attr('stroke-opacity', 0.2)
        .attr('stroke-width', 3)
        .transition().duration(600)
        .attr('r', rootR + 12);

      // Pulsing outer ring
      rootG.append('circle')
        .attr('r', 0)
        .attr('fill', 'none')
        .attr('stroke', '#818CF8')
        .attr('stroke-opacity', 0.4)
        .attr('stroke-width', 2)
        .transition().duration(600)
        .attr('r', rootR + 6);

      // Main circle
      rootG.append('circle')
        .attr('r', 0)
        .attr('fill', 'url(#center-grad)')
        .attr('filter', 'url(#glow)')
        .attr('stroke', 'rgba(255,255,255,0.3)')
        .attr('stroke-width', 2)
        .transition().duration(500)
        .attr('r', rootR);

      // Name
      const rootName = rootNode.name?.length > 10 ? rootNode.name.substring(0, 9) + '..' : (rootNode.name || 'You');
      rootG.append('text')
        .attr('text-anchor', 'middle').attr('dominant-baseline', 'central')
        .attr('fill', '#fff').attr('font-size', 11).attr('font-weight', 'bold')
        .attr('font-family', 'sans-serif')
        .text(rootName)
        .attr('opacity', 0)
        .transition().duration(400).delay(300)
        .attr('opacity', 1);

      // Downline count below
      rootG.append('text')
        .attr('y', rootR + 16)
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(129, 140, 248, 0.7)')
        .attr('font-size', 10)
        .text(`${rootNode.totalDownline} ${language === 'fr' ? 'membres' : 'members'}`)
        .attr('opacity', 0)
        .transition().duration(400).delay(500)
        .attr('opacity', 1);

      // Rank below that
      if (rootNode.rank) {
        rootG.append('text')
          .attr('y', rootR + 28)
          .attr('text-anchor', 'middle')
          .attr('fill', RANK_COLORS[rootNode.rank] || '#9CA3AF')
          .attr('font-size', 9).attr('font-weight', 'bold')
          .text(rootNode.rank.charAt(0).toUpperCase() + rootNode.rank.slice(1))
          .attr('opacity', 0)
          .transition().duration(400).delay(600)
          .attr('opacity', 0.8);
      }

      // Pulsing animation on outer halo
      function pulse() {
        rootG.select('circle:first-child')
          .transition().duration(2000)
          .attr('r', rootR + 18).attr('stroke-opacity', 0.08)
          .transition().duration(2000)
          .attr('r', rootR + 12).attr('stroke-opacity', 0.2)
          .on('end', pulse);
      }
      setTimeout(pulse, 1000);
    }

    // Reset zoom on double-click
    svg.on('dblclick.zoom', null);
    svg.on('dblclick', () => {
      svg.transition().duration(500).call(
        zoomBehavior.transform,
        d3.zoomIdentity.translate(cx, cy)
      );
    });

  }, [tree, matrixWidth, renderKey, language, t, onDrillDown]);

  return (
    <div ref={containerRef} className="relative w-full h-full" style={{ minHeight: 450 }}>
      <svg
        ref={svgRef}
        className="w-full h-full bg-[#0B0E17] rounded-xl"
        style={{ touchAction: 'none', display: 'block' }}
        data-testid="orbit-tree-svg"
      />
      {/* Tooltip */}
      {tooltip && !tooltip.data.isEmpty && (
        <div
          className="absolute pointer-events-none z-50 bg-[#1E293B]/95 border border-indigo-500/30 rounded-lg px-3 py-2 shadow-xl backdrop-blur-sm"
          style={{
            left: Math.min(tooltip.x + 12, 300),
            top: Math.max(tooltip.y - 60, 8),
          }}
          data-testid="orbit-tree-tooltip"
        >
          <p className="text-white text-sm font-semibold">{tooltip.data.name}</p>
          <div className="flex items-center gap-2 mt-1">
            {tooltip.data.rank && (
              <span className="text-xs px-1.5 py-0.5 rounded capitalize"
                style={{ color: RANK_COLORS[tooltip.data.rank], backgroundColor: (RANK_COLORS[tooltip.data.rank] || '#666') + '20' }}>
                {tooltip.data.rank}
              </span>
            )}
            {tooltip.data.position && (
              <span className="text-xs text-gray-400">Pos: {tooltip.data.position}</span>
            )}
          </div>
          <div className="flex gap-3 mt-1 text-[10px] text-gray-400">
            <span>{t('Orbite', 'Orbit')} {tooltip.data.level}</span>
            <span>{tooltip.data.totalDownline} {t('en reseau', 'downline')}</span>
          </div>
          {tooltip.data.level > 0 && (
            <p className="text-[10px] text-indigo-400 mt-1">{t('Cliquez pour explorer', 'Click to explore')}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default OrbitTreeD3;
